import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { IconChevronDown } from '@rossum/ui/icons/tabler';
import {
  Button,
  CircularProgress,
  InputAdornment,
  Popover,
  Stack,
  SvgIcon,
  TextField,
} from '@rossum/ui/material';
import { useWorkspacesWithQueues } from '../../../queues/hooks/useWorkspacesWithQueues';
import { QueueSelectList } from '../../../queues/select-queue/QueueSelectList';

export const UserQueueSelect = ({
  selectedQueues,
  setSelectedQueues,
  disabled,
  dataCy,
}: {
  selectedQueues: number[];
  setSelectedQueues: (queue: number[]) => void;
  disabled?: boolean;
  dataCy?: string;
}) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { workspacesWithQueues, isLoading } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const queuesByWorkspace = useMemo(
    () => workspacesWithQueues ?? [],
    [workspacesWithQueues]
  );

  const handleSelectQueues = useCallback(
    (ids: number[]) => {
      setSelectedQueues(ids);
    },
    [setSelectedQueues]
  );

  const queuesIds = useMemo(() => {
    return queuesByWorkspace.flatMap(workspace =>
      workspace.queues.map(queue => queue.id)
    );
  }, [queuesByWorkspace]);

  const allSelected = useMemo(() => {
    if (queuesByWorkspace.length === 0) return false;
    return queuesByWorkspace.every(workspace =>
      workspace.queues.every(queue => selectedQueues.includes(queue.id))
    );
  }, [queuesByWorkspace, selectedQueues]);

  const displayValue = useMemo(() => {
    if (selectedQueues.length === 1) {
      const selectedQueue = queuesByWorkspace
        .flatMap(ws => ws.queues.map(q => ({ ws, q })))
        .find(({ q }) => selectedQueues.includes(q.id));

      return `${selectedQueue?.q.name} (${selectedQueue?.ws.name})`;
    }
    if (allSelected) {
      return intl.formatMessage({
        id: 'features.users.selectQueueChip.allQueuesSelected',
      });
    }
    if (selectedQueues.length > 1) {
      return intl.formatMessage(
        {
          id: 'features.users.selectQueueChip.multipleQueuesSelected',
        },
        {
          count: selectedQueues.length,
        }
      );
    }
    return '';
  }, [selectedQueues, queuesByWorkspace, allSelected, intl]);

  return (
    <>
      <TextField
        fullWidth
        size="small"
        disabled={disabled || isLoading}
        data-cy={dataCy}
        value={displayValue}
        placeholder={intl.formatMessage({
          id: 'features.users.selectQueueChip.selectQueue',
        })}
        onClick={event => {
          if (disabled || isLoading) return;
          setAnchorEl(event.currentTarget);
        }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <SvgIcon fontSize="medium">
                  <IconChevronDown />
                </SvgIcon>
              )}
            </InputAdornment>
          ),
        }}
      />

      {queuesByWorkspace.length > 0 ? (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Stack spacing={1} p={2} sx={{ width: '400px' }}>
            <Stack pt={1} maxHeight="30vh" overflow="auto">
              <QueueSelectList
                topSelections={selectedQueues}
                queuesByWorkspace={queuesByWorkspace}
                onSelect={handleSelectQueues}
                selectedQueues={selectedQueues}
                isLoading={isLoading}
                multiSelect
                autoFocus
              />
            </Stack>
            <Button
              onClick={() => {
                setAnchorEl(null);
                if (allSelected) {
                  handleSelectQueues([]);
                } else {
                  handleSelectQueues(queuesIds);
                }
              }}
              variant="outlined"
              color="secondary"
            >
              {allSelected
                ? intl.formatMessage({
                    id: 'features.users.selectQueue.deselectAll.button.label',
                  })
                : intl.formatMessage({
                    id: 'features.users.selectQueue.selectAll.button.label',
                  })}
            </Button>
          </Stack>
        </Popover>
      ) : null}
    </>
  );
};
